import React, { useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import ModuleSection from '../ModuleSection';
import blogStorage from '../../utils/storage';
import './index.scss';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [timeRange, setTimeRange] = useState('all'); // all, 3months, 6months, year
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadAnalytics();
    loadCategories();
  }, []);

  const loadAnalytics = () => {
    const analyticsData = blogStorage.getAnalytics();
    setAnalytics(analyticsData);
  };

  const loadCategories = () => {
    const categoriesData = blogStorage.getCategories();
    setCategories(categoriesData);
  };

  const handleRefresh = async () => {
    setIsSaving(true);
    try {
      // 刷新统计数据
      await new Promise(resolve => setTimeout(resolve, 500)); // 模拟异步操作
      blogStorage.updateAnalytics();
      loadAnalytics();
    } finally {
      setIsSaving(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + 'w';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  if (!analytics) {
    return (
      <div className="analytics">
        <div className="loading">加载统计数据中...</div>
      </div>
    );
  }

  return (
    <div className="analytics">
      {/* 数据概览 */}
      <ModuleSection
        title="统计分析"
        headerExtra={`最后更新: ${new Date(analytics.lastUpdated).toLocaleString('zh-CN')}`}
        actions={
          <Button 
            type="primary"
            onClick={handleRefresh}
            disabled={isSaving}
          >
            {isSaving ? '刷新中...' : '刷新数据'}
          </Button>
        }
        variant="data"
      >
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📝</div>
              <div className="stat-content">
                <div className="stat-number">{analytics.totalArticles}</div>
                <div className="stat-label">总文章数</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">👀</div>
              <div className="stat-content">
                <div className="stat-number">{formatNumber(analytics.totalViews)}</div>
                <div className="stat-label">总浏览量</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">❤️</div>
              <div className="stat-content">
                <div className="stat-number">{formatNumber(analytics.totalLikes)}</div>
                <div className="stat-label">总点赞数</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">✍️</div>
              <div className="stat-content">
                <div className="stat-number">{formatNumber(analytics.totalWords)}</div>
                <div className="stat-label">总字数</div>
              </div>
            </div>
          </div>
      </ModuleSection>

      {/* 发布趋势 */}
      {analytics.monthlyStats && analytics.monthlyStats.length > 0 && (
        <div className="module-section trend-section">
          <div className="section-header">
            <h4>发布趋势</h4>
          </div>
          <div className="section-content">
            <div className="trend-chart">
              <div className="chart-container">
                {analytics.monthlyStats.slice(-12).map((month, index) => (
                  <div key={month.month} className="chart-bar">
                    <div 
                      className="bar"
                      style={{
                        height: `${(month.articles / Math.max(...analytics.monthlyStats.map(m => m.articles))) * 100}%`
                      }}
                      title={`${month.month}: ${month.articles}篇`}
                    />
                    <div className="bar-label">{month.month.slice(-2)}月</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 分类统计 */}
      {analytics.categoryStats && analytics.categoryStats.length > 0 && (
        <div className="module-section category-section">
          <div className="section-header">
            <h4>分类统计</h4>
          </div>
          <div className="section-content">
            <div className="category-stats">
              {analytics.categoryStats.map(stat => (
                <div key={stat.category} className="category-stat">
                  <div className="category-info">
                    <span className="category-name">{stat.category}</span>
                    <span className="category-count">{stat.count} 篇 ({stat.percentage}%)</span>
                  </div>
                  <div className="category-bar">
                    <div 
                      className="bar-fill"
                      style={{ width: `${stat.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 热门标签 */}
      {analytics.tagStats && analytics.tagStats.length > 0 && (
        <div className="module-section tags-section">
          <div className="section-header">
            <h4>热门标签</h4>
          </div>
          <div className="section-content">
            <div className="tags-cloud">
              {analytics.tagStats.slice(0, 20).map(tagStat => (
                <div 
                  key={tagStat.tag} 
                  className="tag-item"
                  style={{
                    fontSize: `${12 + (tagStat.count / Math.max(...analytics.tagStats.map(t => t.count))) * 8}px`
                  }}
                >
                  {tagStat.tag} ({tagStat.count})
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 详细数据表格 */}
      <div className="module-section details-section">
        <div className="section-header">
          <h4>详细数据</h4>
        </div>
        <div className="section-content">
          {analytics.monthlyStats && analytics.monthlyStats.length > 0 && (
            <div className="data-table">
              <h5>月度统计</h5>
              <table>
                <thead>
                  <tr>
                    <th>月份</th>
                    <th>文章数</th>
                    <th>浏览量</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.monthlyStats.slice(-6).reverse().map(month => (
                    <tr key={month.month}>
                      <td>{month.month}</td>
                      <td>{month.articles}</td>
                      <td>{month.views}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {analytics.categoryStats && analytics.categoryStats.length > 0 && (
            <div className="data-table">
              <h5>分类详情</h5>
              <table>
                <thead>
                  <tr>
                    <th>分类</th>
                    <th>文章数</th>
                    <th>占比</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.categoryStats.map(stat => (
                    <tr key={stat.category}>
                      <td>{stat.category}</td>
                      <td>{stat.count}</td>
                      <td>{stat.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;